import { useMutation, useQuery } from "@tanstack/react-query";
import { useAppState } from "Context/StateContext";
import { CompanyConfig } from "Models/company.config";
import ChecklistService from "Services/checklist.service";
import { CompanyConfigsService } from "Services/company-configs.service";
import ReportsGroupsService from "Services/reports.groups.service";
import { Status } from "Util/enums";
import { CheckListKeys, ReportGroupKeys } from "Util/queries";

const CACHE_TIME = 60 * 60 * 1000 * 24;

export const useOperationsAppSettingsActions = () => {
  const { currentUser } = useAppState();

  const getCompanyConfigs = () => {
    return useQuery(
      ["company.configs", currentUser?.companyId],
      () =>
        CompanyConfigsService.getCurrent().then(({ data }) => data),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
      }
    );
  }

  const saveCompanyConfigs = (options: { handleSuccess?: () => void } = {}) => {
    const { handleSuccess } = options
    return useMutation(
      ["company.configs"],
      (payload: Partial<CompanyConfig>) =>
        CompanyConfigsService.saveCurrent(payload).then(({ data }) => data),
      {
        onSuccess: () => {
          handleSuccess?.()
        },
      }
    );
  }

  const getAllReportGroup = () => {
    return useQuery({
      queryKey: ReportGroupKeys.getAll([currentUser?.company?.profileId]),
      queryFn: async () => {
        const response = await ReportsGroupsService.getAll({
          companyProfileId: currentUser?.company?.profileId,
          include: JSON.stringify([
            { association: "reports", include: [{ association: "reportGroup" }] },
          ]),
        });

        return response?.data;
      },
      enabled: !!currentUser?.id && !!currentUser?.company?.profileId,
      refetchOnWindowFocus: false,
      staleTime: 30 * 60 * 1000,
      cacheTime: CACHE_TIME,
    });
  };

  const getAllCheckList = () => {
    return useQuery({
      queryKey: CheckListKeys.getAll([
        Status.ACTIVE,
        currentUser?.companyId,
        "description",
        "desc",
      ]),
      queryFn: async () =>
        ChecklistService.getAll({
          status: Status.ACTIVE,
          companyId: currentUser?.companyId,
          sortBy: JSON.stringify([["description", "desc"]]),
        }),
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      staleTime: 30 * 60 * 1000,
      cacheTime: CACHE_TIME,
    });
  };

  return {
    getCompanyConfigs,
    getAllCheckList,
    getAllReportGroup,
    saveCompanyConfigs,
  }
}
